import "react-dual-listbox/lib/react-dual-listbox.css";
import React, { useCallback, useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useSelector } from "react-redux";
import Loading from "react-fullscreen-loading";

// region Imports - Components
import Header from "@components/Header";
import ButtonTable from "@components/Button/ButtonTable";

import { DataTableButtons, DataTableColumns, DataTableActions } from "@molecules/NewDataTable";

import UserTable from "./components/organisms/UserTable";
import UserFormModal from "./components/organisms/UserFormModal";
import ConfirmDeleteDialog from "./components/organisms/ConfirmDeleteDialog";
import ConfirmValidationEmailDialog from "./components/organisms/ConfirmValidationEmailDialog";
// endregion Imports - Components
// region Imports - External libraries
import { Grid } from "@material-ui/core";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
// endregion Imports - External libraries
// region Imports - Hooks
import { useAuth } from "@hooks/useAuth";
import { useToast } from "@hooks/useToast";
import { usePlantRegionalSync } from "./hooks/usePlantRegionalSync";
import { useUsersList } from "./hooks/useUsersList";
import { useUserForm } from "./hooks/useUserForm";
// endregion Imports - Hooks
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { MenuMessages, ManagementMessages, DataTableMessages, GlobalMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared / Utils
import { User } from "@shared/interfaces/user.interface";
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
import utils from "@utils/useful-functions";

import { getDualListboxLang, getFormattedRestrictVehicleOptions, getUserProfileLabel, dataTableSettings } from "./constants";
// endregion Imports - Shared / Utils
// region Imports - Styles
import { Container, ContainerModalFormUser } from "./styles";
// endregion Imports - Styles

const Users: React.FC = () => {

  // region Hooks
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { user: loggedUser } = useAuth();
  const screen = useSelector((state: any) => state.screen);
  const plantSync = usePlantRegionalSync({ addToast, t });
  const {
    users,
    loadingUserList,
    table,
    setTable,
    tableClickedRow,
    setTableClickedRow,
    readUsers,
    deleteUser: deleteUserApi
  } = useUsersList({ addToast, t });
  const userForm = useUserForm({
    t,
    addToast,
    readUsers: () => { void readUsers(); },
    table,
    tableClickedRow,
    activeOptionsPlants: plantSync.activeOptionsPlants,
    setActiveOptionsPlants: plantSync.setActiveOptionsPlants
  });
  // endregion Hooks
  // region States
  const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User>({} as User);
  // endregion States
  // region Constants
  const dataTableActions: DataTableActions[] = [
    {
      ref: ".modules-users-list-details",
      callback: (rowData: User) => userForm.handleUserDetails(rowData)
    },
    {
      ref: ".modules-users-list-delete",
      callback: (rowData: User) => handleUserDeleteInit(rowData)
    }
  ];
  const dataTableColumns: DataTableColumns[] = [
    { // Nome
      title: t(ManagementMessages.dataTableName),
      data: (user: User) => user.name,
      defaultContent: "",
      filterable: true,
      propertyName: "name"
    },
    { // Email
      title: t(ManagementMessages.dataTableEmail),
      data: (user: User) => user.email,
      defaultContent: "",
      filterable: true,
      propertyName: "email"
    },
    { // Data de cadastro
      title: t(ManagementMessages.dataTableRegisterDate),
      data: (user: User) => user.registration_date,
      render: (data: any, type: any) => (type === "sort" ? data : utils.formatDateIfHave(new Date(data), "fullDate")),
      defaultContent: "",
      filterable: true,
      propertyName: "registration_date"
    },
    { // Ultimo acesso
      title: t(ManagementMessages.dataTableLastAccess),
      data: (user: User) => user.last_access,
      render: (data: any, type: any) => (type === "sort" ? data : utils.formatDateIfHave(new Date(data), "fullDate")),
      defaultContent: "",
      filterable: true,
      propertyName: "last_access"
    },
    { // Ativo
      title: t(DataTableMessages.status),
      data: (user: User) => (
        ReactDOMServer.renderToString(
          <b style={{ color: user.active ? "rgb(70, 193, 125)" : "rgb(230, 74, 25)" }}>
            {user.active ? t(GlobalMessages.active).toUpperCase() : t(GlobalMessages.inactive).toUpperCase()}
          </b>
        )
      ),
      filterable: true,
      propertyName: "active"
    },
    { // Perfil
      title: t(ManagementMessages.dataTableProfile),
      data: (user: User) => (
        user.admin || user.super_admin
          ? ReactDOMServer.renderToString(
            <b style={{ color: "rgb(245, 124, 0)" }}>{getUserProfileLabel(t, user).toUpperCase()}</b>
          )
          : getUserProfileLabel(t, user)
      ),
      defaultContent: "",
      filterable: true,
      propertyName: "admin",
      toFilterValue: (user: User) => getUserProfileLabel(t, user)
    },
    { // Criado por
      title: t(GlobalMessages.columnCreatedBy),
      data: (user: User) => user?.userCreatedBy?.name ?? "",
      filterable: true,
      defaultContent: "",
      propertyName: "userCreatedBy.name"
    },
    { // Ultima atualização por
      title: t(GlobalMessages.columnLastUpdatedBy),
      data: (user: User) => user?.userLastModifiedBy?.name ?? "",
      filterable: true,
      defaultContent: "",
      propertyName: "userLastModifiedBy.name"
    },
    { // Ações
      title: t(DataTableMessages.actions),
      orderable: false,
      searchable: false,
      data: () => ReactDOMServer.renderToString(
        <Grid container spacing={1}>
          <Grid item xs sm md lg xl>
            <ButtonTable
              className="action-button modules-users-list-details"
            >
              <FormatListBulletedIcon />
            </ButtonTable>
          </Grid>
          <Grid item xs sm md lg xl>
            <ButtonTable
              className="action-button modules-users-list-delete"
            >
              <DeleteForeverIcon />
            </ButtonTable>
          </Grid>
        </Grid>
      ),
      width: "130px",
      filterable: false
    }
  ];
  const dataTableButtons: DataTableButtons[] = useMemo(() => [
    {
      name: t(DataTableMessages.buttonsAddNew),
      key: "add",
      callback: () => userForm.handleUserCreate()
    },
    {
      name: t(DataTableMessages.buttonsRefresh),
      key: "refresh",
      callback: () => void readUsers()
    },
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print",
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => utils.clickButtonDomElement("button-export"),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `relatorio_usuarios_${new Date().toISOString().split("T")[0]}`,
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    }
  ], [t, userForm, readUsers]);

  const dualListboxLang = useMemo(() => getDualListboxLang(t), [t]);
  const formattedRestrictVehicleOptions = useMemo(() =>
    getFormattedRestrictVehicleOptions(t, userForm.optionsRestrictVehicles),
  [t, userForm.optionsRestrictVehicles]);
  // endregion Constants
  // region Functions

  /**
   * A callback function to initialize the user deletion process.
   *
   * @param {User} user - The user object selected for deletion.
   */
  const handleUserDeleteInit = useCallback((user: User) => {
    setUserToDelete(user);
    setOpenDialogConfirmDelete(true);
  }, []);

  /**
   * Retrieves the confirmation email address from the user form.
   *
   * This function attempts to access the current value of the "email" field
   * from the `userForm`'s `formRef`. If the `formRef` is not available or
   * the field cannot be retrieved, it returns an empty string. Errors during
   * the process are safely caught and ignored.
   */
  const getConfirmationEmail = () => {
    try {
      return userForm.formRef.current?.getFieldValue ? userForm.formRef.current?.getFieldValue("email") : "";
    } catch(e) {
      console.error("Error retrieving confirmation email:", e);

      return ""; // Ignore the error and return an empty string
    }
  };
  // endregion Functions

  return (
    <>
      <Header title={t(MenuMessages.users)} />
      <Container platform={screen.platform as ScreenPlatform} className="page">
        <Loading loading={userForm.loadingUserDetails} />
        <Loading loading={loadingUserList} />
        <UserTable
          t={t}
          users={users}
          loadingUserList={loadingUserList}
          dataTableColumns={dataTableColumns}
          dataTableSettings={dataTableSettings}
          dataTableActions={dataTableActions}
          dataTableButtons={dataTableButtons}
          setTable={setTable}
          setTableClickedRow={setTableClickedRow}
        />
        <ConfirmDeleteDialog
          open={openDialogConfirmDelete}
          onClose={() => setOpenDialogConfirmDelete(false)}
          onConfirm={() => deleteUserApi(userToDelete, () => setOpenDialogConfirmDelete(false))}
          title={t(ManagementMessages.deleteConfirmationTitle)}
          userDetails={userToDelete}
          t={t}
        />
        <ConfirmValidationEmailDialog
          open={userForm.openDialogConfirmSendValidationEmail}
          onClose={() => userForm.setOpenDialogConfirmSendValidationEmail(false)}
          onConfirm={() => userForm.createUser(userForm.formRef.current?.getData() as User, true)}
          title={t(ManagementMessages.validationTitle)}
          email={getConfirmationEmail()}
          t={t}
        />
        <ContainerModalFormUser id="modalFormUser">
          <UserFormModal
            t={t}
            loggedUser={loggedUser}
            {...userForm}
            {...plantSync}
            formattedRestrictVehicleOptions={formattedRestrictVehicleOptions}
            dualListboxLang={dualListboxLang}
            screenPlatform={screen.platform}
          />
        </ContainerModalFormUser>
      </Container>
    </>
  );
};

export default Users;
